import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { SiOpensourcehardware } from "react-icons/si";
import { FaFileInvoiceDollar } from "react-icons/fa";
import { FaSpinner } from "react-icons/fa";
import { FaBell } from "react-icons/fa";


/* ---------------- CONSTANTS ---------------- */
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const PARKING_CONCEPT_ID = "2980";
const PARKING_CONCEPT_GROUP_1 = ["125", "142", "428", "33", "153", "156"];

/* ---------------- COMPONENT ---------------- */
const HardwareInvoiceTab = () => {
  const [customers, setCustomers] = useState([]);
  const [garages, setGarages] = useState([]);

  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedGarage, setSelectedGarage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [approvedQuotations, setApprovedQuotations] = useState([]);
  const [readyInvoiceNotifications, setReadyInvoiceNotifications] = useState([]);
  const [showInvoiceNotifications, setShowInvoiceNotifications] = useState(false);
  const invoiceNotificationRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [quotationLoading, setQuotationLoading] = useState(false);


  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const [emailForm, setEmailForm] = useState({
    email: "",
    name: "",
    title: "",
  });
  const [selectedInvoiceContext, setSelectedInvoiceContext] = useState(null);

  /* ---------------- GROUP LOGIC ---------------- */
  const isParkingConcept = selectedCustomer === PARKING_CONCEPT_ID;

  const isGroup1Garage = (garageId) =>
    PARKING_CONCEPT_GROUP_1.includes(String(garageId));

  const isGroup2Garage = (garageId) =>
    !PARKING_CONCEPT_GROUP_1.includes(String(garageId));

  /* ---------------- API LOADERS ---------------- */
  const loadCustomers = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/invoice/customers/`);
      setCustomers(res.data);
    } catch (err) {
      console.error("Customer load error:", err);
    }
  };

  const loadGarages = async (customerID) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/invoice/garages/?customer_id=${customerID}`
      );
      setGarages(res.data || []);
    } catch (err) {
      console.error("Garage load error:", err);
      setGarages([]);
    }
  };

  const checkReadyToInvoiceQuotations = async (customerList) => {
    try {
      const notifications = [];

      await Promise.all(customerList.map(async (customer) => {
        const garagesResponse = await axios.get(
          `${BASE_URL}/invoice/garages/?customer_id=${customer.CustomerID}`
        );

        await Promise.all((garagesResponse.data || []).map(async (garage) => {
          const response = await axios.get(`${BASE_URL}/quotations/approved-quotation`, {
            params: {
              customer_id: String(customer.CustomerID),
              garage_id: String(garage.garage_id),
            },
          });

          (response.data.data || [])
            .filter((quotation) => quotation.hardware_email_status !== "Sent")
            .forEach((quotation) => {
            notifications.push({
              customerId: String(customer.CustomerID),
              garageId: String(garage.garage_id),
              garageName: garage.garage_name,
              customerName: customer.CustomerName,
              quotationId: quotation.id,
              quotationNumber: quotation.quotation_number,
            });
            });
        }));
      }));

      setReadyInvoiceNotifications(notifications);
    } catch (err) {
      console.error("Ready-to-invoice notification error:", err);
    }
  };

  /* ---------------- LOAD APPROVED QUOTATIONS ---------------- */
  const loadApprovedQuotations = async () => {
    if (!selectedCustomer || !selectedGarage) return;

    setQuotationLoading(true);

    try {
      const res = await axios.get(`${BASE_URL}/quotations/approved-quotation`, {
        params: {
          customer_id: selectedCustomer,
          garage_id: selectedGarage,
        },
      });

      setApprovedQuotations(
        (res.data.data || []).filter((quotation) => quotation.hardware_email_status !== "Sent")
      );
    } catch (err) {
      console.error("Approved quotation load error:", err);
      setApprovedQuotations([]);
    }

    setQuotationLoading(false);
  };



  // email Preview Modal
  const handleOpenEmailModal = (quotation, partialBatch = null) => {
    if (!selectedCustomer || !selectedGarage) {
      alert("Please select customer and garage.");
      return;
    }

    if (!quotation) {
      alert("No approved quotation found.");
      return;
    }

    setSelectedInvoiceContext({
      quotationId: quotation.id,
      partialInvoiceId: partialBatch?.partial_invoice_id || null,
    });

    setEmailForm({
      email: quotation.customer?.RepresentativeEmail || "",
      name: quotation.customer?.RepresentativeName || "",
      title: quotation.customer?.RepresentativeTitle || "",
    });

    setShowEmailModal(true);
  };

  // send email 

  const sendHardwareEmail = async () => {
    if (!emailForm.email) {
      alert("Email is required");
      return;
    }

    setEmailLoading(true);

    try {
      await axios.post(
        `${BASE_URL}/invoice/send-hardware-invoice-email/`,
        {
          email: emailForm.email,
          name: emailForm.name,
        },
        {
          params: {
            customer_id: selectedCustomer,
            garage_id: selectedGarage,
            partial_invoice_id: selectedInvoiceContext?.partialInvoiceId,
          },
        }
      );

      alert("Email sent successfully ✅");
      setShowEmailModal(false);
      setReadyInvoiceNotifications((notifications) =>
        notifications.filter((notification) => notification.quotationId !== selectedInvoiceContext?.quotationId)
      );
      await loadApprovedQuotations();

    } catch (error) {
      console.error(error);
      alert("Failed to send email ❌");
    } finally {
      setEmailLoading(false);
      setSelectedInvoiceContext(null);
    }

    setEmailLoading(false);
  };

  /* ---------------- EFFECTS ---------------- */
  useEffect(() => {
    const loadCustomersAndNotifications = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/invoice/customers/`);
        const customerList = res.data || [];
        setCustomers(customerList);
        await checkReadyToInvoiceQuotations(customerList);
      } catch (err) {
        console.error("Customer load error:", err);
      }
    };

    loadCustomersAndNotifications();
  }, []);

  useEffect(() => {
    if (!selectedCustomer) {
      setGarages([]);
      setSelectedGarage("");
      return;
    }

    loadGarages(selectedCustomer);
    setSelectedGarage("");
    setApprovedQuotations([]);
  }, [selectedCustomer]);

  useEffect(() => {
    if (selectedCustomer && selectedGarage) {
      loadApprovedQuotations();
    }
  }, [selectedGarage]);

  useEffect(() => {
    if (!showInvoiceNotifications) return undefined;

    const handleOutsideClick = (event) => {
      if (!invoiceNotificationRef.current?.contains(event.target)) {
        setShowInvoiceNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showInvoiceNotifications]);

  /* ---------------- GENERATE PDF ---------------- */
  const generateHardwarePDF = async (partialBatch = null) => {
    if (!selectedCustomer || !selectedGarage) {
      alert("Please select customer and garage.");
      return;
    }

    setLoading(true);

    try {
      const res = await axios.get(
        `${BASE_URL}/invoice/generate-hardware-invoice-pdf/`,
        {
          params: {
            customer_id: selectedCustomer,
            garage_id: selectedGarage,
            partial_invoice_id: partialBatch?.partial_invoice_id || null,
          },
          responseType: "blob",
        }
      );

      const contentDisposition = res.headers["content-disposition"];
      let filename = "hardware_invoice.pdf";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match?.[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

    } catch (err) {
      console.error("PDF generation error:", err);
      alert("Failed to generate PDF.");
    }

    setLoading(false);
  };



  /* ---------------- UI ---------------- */
  const invoiceCards = approvedQuotations.flatMap((quotation) => {
    const batches = quotation.pending_partial_batches?.length
      ? quotation.pending_partial_batches
      : quotation.pending_partial_invoice
        ? [{
          partial_invoice_id: quotation.pending_partial_invoice_id,
          ...quotation.pending_partial_invoice,
        }]
        : [null];

    return batches.map((partialBatch, index) => ({
      quotation,
      partialBatch,
      cardKey: `${quotation.id}-${partialBatch?.partial_invoice_id || index}`,
    }));
  });

  return (
    <div className="p-2">
      <div className="bg-white shadow rounded-xl p-5 mb-6 border">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <SiOpensourcehardware /> Hardware Invoice
          {readyInvoiceNotifications.length > 0 && (
            <div ref={invoiceNotificationRef} className="relative ml-auto">
              <button
                type="button"
                onClick={() => setShowInvoiceNotifications((isOpen) => !isOpen)}
                className="relative text-amber-600 hover:text-amber-700"
                title="Approved quotations ready to invoice"
                aria-label="Approved quotations ready to invoice"
              >
                <FaBell size={22} />
                <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-xs text-white">
                  {readyInvoiceNotifications.length}
                </span>
              </button>

              {showInvoiceNotifications && (
                <div className="absolute right-0 top-8 z-20 w-80 rounded border bg-white p-3 text-sm shadow-lg">
                  <p className="mb-2 font-semibold text-gray-800">
                    Approved quotations ready to invoice
                  </p>
                  {readyInvoiceNotifications.map((item) => (
                    <div key={`${item.customerId}-${item.quotationId}`} className="border-t py-2 text-gray-700">
                      <p className="font-medium">{item.customerName}</p>
                      <p>{item.garageName} - {item.quotationNumber}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </h2>


        {/* CUSTOMER */}
        <div className="mt-4">
          <label className="font-semibold">Customer</label>
          <select
            className="border rounded p-2 w-full mt-1"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
          >
            <option value="">Select Customer</option>
            {customers.map((c) => (
              <option key={c.CustomerID} value={String(c.CustomerID)}>
                {c.CustomerName}
              </option>
            ))}
          </select>
        </div>

        {/* GARAGES */}
        {selectedCustomer && garages.length > 0 && (
          <div className="mt-4">
            <label className="font-semibold block mb-2">
              Select Garage
            </label>

            <div className="mb-2 relative">
              <input
                type="text"
                placeholder="Search garages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border rounded p-2 w-full pr-10"
              />

            </div>

            <div className="grid grid-cols-2 gap-2 border rounded p-3 max-h-48 overflow-y-auto">
              {garages.filter(g => g.garage_name.toLowerCase().includes(searchTerm.toLowerCase())).map((g) => {
                const garageIdStr = String(g.garage_id);

                const disabled =
                  isParkingConcept &&
                  selectedGarage &&
                  (
                    (isGroup1Garage(garageIdStr) &&
                      isGroup2Garage(selectedGarage)) ||
                    (isGroup2Garage(garageIdStr) &&
                      isGroup1Garage(selectedGarage))
                  );

                return (
                  <label
                    key={garageIdStr}
                    className={`flex items-center gap-2 ${disabled ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                  >
                    <input
                      type="radio"
                      name="garage"
                      checked={selectedGarage === garageIdStr}
                      disabled={disabled}
                      onChange={() => setSelectedGarage(garageIdStr)}
                    />
                    {g.garage_name}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* SHOW APPROVED QUOTATIONS */}
        {quotationLoading && (
          <p className="mt-4 text-blue-600">Loading approved quotations...</p>
        )}

        {approvedQuotations.length > 0 && (
          <div className="mt-4 border rounded p-3 bg-gray-50">
            <p className="font-semibold mb-2">
              Pending Invoice Records Found: {invoiceCards.length}
            </p>

            {invoiceCards.map(({ quotation: q, partialBatch, cardKey }) => {
              const itemsSubtotal = (q.items || []).reduce((subtotal, item) => {
                const invoiceQuantity = partialBatch
                  ? (partialBatch.items.find((partialItem) => partialItem.quotation_item_id === item.id)?.delivered_quantity || 0)
                  : item.quantity;
                return subtotal + (Number(invoiceQuantity) * Number(item.unit_price || 0));
              }, 0);
              const itemsTax = itemsSubtotal * (Number(q.financials?.items_tax || 0) / 100);
              const itemsTotal = itemsSubtotal + itemsTax;
              const installationSubtotal = Number(q.financials?.total_installation_amount || 0);
              const installationTax = installationSubtotal * (Number(q.financials?.installation_tax || 0) / 100);
              const installationTotal = installationSubtotal + installationTax;
              const shippingCharge = Number(q.financials?.shipping_charge || 0);
              const shippingTax = shippingCharge * (Number(q.financials?.tax_rate || 0) / 100);

              return (
              <div
                key={cardKey}
                className="border rounded-xl p-4 mb-4 bg-white shadow"
              >
                {/* HEADER */}
                <div className="flex justify-between items-center border-b pb-2 mb-3">
                  <div>
                    <h3 className="font-bold text-lg">
                      {q.quotation_number}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {/* Service Start: {q.service_start_date || "N/A"} */}
                      Created at: {q.created_at || "N/A"}
                    </p>
                  </div>

                  {/* <div className="text-right">
                    <p className="text-sm text-gray-500">Grand Total</p>
                    <p className="text-xl font-bold text-green-600">
                      $ {Number(q.financials?.grand_total || 0).toLocaleString("en-IN")}
                    </p>
                  </div> */}
                  {/* GENERATE PDF BUTTON INSIDE CARD */}
                  {/* <div className="mt-4 text-right">
                    <button
                      onClick={generateHardwarePDF}
                      disabled={loading}
                      className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <FaFileInvoiceDollar />
                          Generate Hardware Invoice PDF
                        </>
                      )}
                    </button>
                  </div> */}

                  <div className="mt-4 text-right flex gap-2 justify-end">

                    {/* SEND EMAIL BUTTON */}
                    <button
                      onClick={() => handleOpenEmailModal(q, partialBatch)}
                      disabled={q.hardware_email_status === "Sent"}
                      className={`px-4 py-2 rounded text-white flex items-center gap-2
    ${q.hardware_email_status === "Sent"
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700"
                        }`}
                    >
                      {q.hardware_email_status === "Sent"
                        ? "Email Sent "
                        : "Send Invoice Email"}
                    </button>

                    {/* GENERATE PDF BUTTON */}
                    <button
                      onClick={() => generateHardwarePDF(partialBatch)}
                      disabled={loading}
                      className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <FaFileInvoiceDollar />
                          Generate Hardware Invoice PDF
                        </>
                      )}
                    </button>
                  </div>

                </div>

                {q.ApprovedComments?.trim() && (
                  <div className="mb-4 rounded border-l-4 border-blue-500 bg-blue-50 p-3">
                    <h4 className="mb-1 text-sm font-semibold text-blue-900">Message from Sales</h4>
                    <p className="whitespace-pre-wrap break-words text-sm text-gray-800">
                      {q.ApprovedComments}
                    </p>
                  </div>
                )}

                {partialBatch && (
                  <div className="mb-4 rounded border-l-4 border-green-500 bg-green-50 p-3">
                    <h4 className="mb-1 text-sm font-semibold text-green-900">Partial invoice ready</h4>
                    <p className="text-sm text-gray-800">
                      The invoice will include only the delivered quantities in this pending batch.
                    </p>
                    {partialBatch.sales_comments?.trim() && (
                      <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-700">
                        Sales comments: {partialBatch.sales_comments}
                      </p>
                    )}
                  </div>
                )}

                {/* CAPITAL ITEMS TABLE */}
                {q.items?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2">Capital Items</h4>
                    <div className="overflow-x-auto border rounded">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left">SKU</th>
                            <th className="px-4 py-2 text-left">Item Name</th>
                            <th className="px-4 py-2 text-left">Description</th>
                            <th className="px-4 py-2 text-center">Approved Qty</th>
                            <th className="px-4 py-2 text-center">Invoice Qty</th>
                            <th className="px-4 py-2 text-right">Unit Price</th>
                            <th className="px-4 py-2 text-right">Total Price</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {q.items.map((item) => (
                            <tr key={item.id}>
                              <td className="px-4 py-2">{item.sku || "-"}</td>
                              <td className="px-4 py-2">{item.item_name}</td>
                              <td className="px-4 py-2">{item.description || "-"}</td>
                              <td className="px-4 py-2 text-center">{item.quantity}</td>
                              <td className="px-4 py-2 text-center font-semibold text-green-700">
                                {partialBatch
                                  ? (partialBatch.items.find((partialItem) => partialItem.quotation_item_id === item.id)?.delivered_quantity || 0)
                                  : item.quantity}
                              </td>
                              <td className="px-4 py-2 text-right">
                                $ {Number(item.unit_price).toLocaleString("en-IN")}
                              </td>
                              <td className="px-4 py-2 text-right font-semibold">
                                $ {Number(item.total_price).toLocaleString("en-IN")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* INSTALLATION EXPENSES TABLE */}
                {q.quotation_installation_expenses?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2">Installation Expenses</h4>
                    <div className="overflow-x-auto border rounded">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left">Expense Name</th>
                            <th className="px-4 py-2 text-center">Qty</th>
                            <th className="px-4 py-2 text-right">Price</th>
                            <th className="px-4 py-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {q.quotation_installation_expenses.map((inst, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2">{inst.expense_name}</td>
                              <td className="px-4 py-2 text-center">{inst.expense_quantity}</td>
                              <td className="px-4 py-2 text-right">
                                $ {Number(inst.price).toLocaleString("en-IN")}
                              </td>
                              <td className="px-4 py-2 text-right font-semibold">
                                $ {(Number(inst.expense_quantity || 0) * Number(inst.price || 0)).toLocaleString("en-IN")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}


                {/* FINANCIAL BREAKDOWN */}
                <div className="border-t pt-3 text-sm">
                  <div className="flex justify-between">
                    <span>Items Subtotal</span>
                    <span>
                      ${" "}
                      {itemsSubtotal.toLocaleString("en-IN")}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span>Items Tax ({q.financials?.items_tax || 0}%)</span>
                    <span>
                      ${" "}
                      {itemsTax.toLocaleString("en-IN")}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span>
                      Items Total
                    </span>
                    <span>
                      ${" "}
                      {itemsTotal.toLocaleString("en-IN")}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span>Installation Subtotal</span>
                    <span>
                      ${" "}
                      {installationSubtotal.toLocaleString("en-IN")}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span>Installation Tax ({q.financials?.installation_tax || 0}%)</span>
                    <span>
                      ${" "}
                      {installationTax.toLocaleString("en-IN")}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span>
                      Installation Total
                    </span>
                    <span>
                      ${" "}
                      {installationTotal.toLocaleString("en-IN")}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span>Shipping charge</span>
                    <span>
                      ${" "}
                      {shippingCharge.toLocaleString("en-IN")}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span>Shipping Tax ({q.financials?.tax_rate || 0}%)</span>
                    <span>
                      ${" "}
                      {shippingTax.toLocaleString("en-IN")}
                    </span>
                  </div>

                  <div className="flex justify-between font-bold text-lg border-t mt-2 pt-2">
                    <span>Estimated Total</span>
                    <span>
                      ${" "}
                      {(
                        itemsTotal +
                        installationTotal +
                        shippingCharge +
                        shippingTax
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        )}


        {showEmailModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-[400px] shadow-lg">

              <h2 className="text-xl font-bold mb-4">
                Send Hardware Invoice
              </h2>

              {/* NAME */}
              <div className="mb-3">
                <label className="block text-sm font-semibold">
                  Representative Name
                </label>
                <input
                  type="text"
                  value={emailForm.name}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, name: e.target.value })
                  }
                  className="border p-2 w-full rounded mt-1"
                />
              </div>

              {/* EMAIL */}
              <div className="mb-4">
                <label className="block text-sm font-semibold">
                  Email
                </label>
                <input
                  type="email"
                  value={emailForm.email}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, email: e.target.value })
                  }
                  className="border p-2 w-full rounded mt-1"
                />
              </div>

              {/* ACTIONS */}
              <div className="flex justify-end gap-2">

                <button
                  onClick={() => setShowEmailModal(false)}
                  className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
                >
                  Cancel
                </button>

                <button
                  onClick={sendHardwareEmail}
                  disabled={emailLoading}
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
                >
                  {emailLoading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Confirm & Send"
                  )}
                </button>

              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HardwareInvoiceTab;